import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Package, Check } from "lucide-react";
import { getShipmentTracking } from "@/lib/orders.functions";
import { deliveryLabel, shipmentStatusLabel, type DeliveryMethod } from "@/lib/orders";
import { BackLink } from "@/components/BackLink";

export const Route = createFileRoute("/order-sparning/$tracking")({
  component: TrackingPage,
  head: () => ({
    meta: [
      { title: "Spåra paket – Sellavo" },
      {
        name: "description",
        content: "Följ paketet från din Sellavo-affär: fraktsedel, inlämning, transport och uthämtning.",
      },
      { property: "og:title", content: "Spåra paket – Sellavo" },
      {
        property: "og:description",
        content: "Följ paketet från din Sellavo-affär steg för steg.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function TrackingPage() {
  const { tracking } = Route.useParams();
  const fetchTracking = useServerFn(getShipmentTracking);

  const { data, isLoading } = useQuery({
    queryKey: ["tracking", tracking],
    queryFn: () => fetchTracking({ data: { tracking } }),
    refetchInterval: 60_000,
  });

  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <BackLink fallbackTo="/mina-affarer" />

      <header className="mt-4 flex items-center gap-3">
        <Package className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">Spåra paket</h1>
      </header>
      <p className="mt-2 font-mono text-sm text-muted-foreground">{tracking}</p>

      {isLoading && <p className="mt-8 text-muted-foreground">Hämtar spårningen…</p>}

      {!isLoading && data && !data.found && (
        <p className="mt-8 rounded-xl border border-border bg-card p-5 text-muted-foreground">
          Vi hittar ingen sändning med det numret. Kolla att du fått med hela numret från
          fraktsedeln.
        </p>
      )}

      {!isLoading && data?.found && (
        <>
          <section className="mt-6 rounded-xl border border-border bg-card p-5">
            <p className="font-semibold text-foreground">{data.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {deliveryLabel(data.method as DeliveryMethod)}
              {data.simulated && " · övningssändning"}
            </p>
          </section>

          <ol className="mt-6 space-y-3">
            {data.events.length === 0 && (
              <li className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                Inga händelser registrerade än. Så fort paketet lämnas in dyker det upp här.
              </li>
            )}
            {data.events.map((e) => (
              <li
                key={`${e.status}-${e.occurred_at}`}
                className="flex gap-3 rounded-xl border border-border bg-card p-4"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ocean-100 text-ocean-700">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{shipmentStatusLabel(e.status)}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(e.occurred_at).toLocaleString("sv-SE")}
                    {e.place && ` · ${e.place}`}
                  </p>
                  {e.note && <p className="mt-1 text-sm text-muted-foreground">{e.note}</p>}
                </div>
              </li>
            ))}
          </ol>

          <p className="mt-6 text-sm text-muted-foreground">
            Är du köpare eller säljare? Logga in så ser du hela affären under{" "}
            <Link to="/mina-affarer" className="text-primary hover:underline">
              mina affärer
            </Link>
            .
          </p>
        </>
      )}
    </main>
  );
}
